import { DashboardEventKind, RawDataUploadEvent } from '../types/events.js';
import {
  FADE_TERMINAL_DOT_MS,
  FAILED_DOT_MS,
  MAX_ROSTER,
  NEW_BADGE_MS,
} from './config.js';
import { currentSpeed } from './state.js';
import type { DomRefs } from './dom.js';
import { escapeHtml, fnvHue, requireQuerySelectorHTML } from './utils.js';

export type DotState = 'pending' | 'processed' | 'failed' | 'cancelled' | 'noTarget';

interface DotRef {
  state: DotState;
  /** この raw_data がどの bucket に属するか (sparkBuckets の index)。-1 で 8h 窓の外。 */
  bucketIdx: number;
}

/** 1 つの大きい丸が何個分の raw_data を表すか */
const DOT_GROUP_SIZE = 10;

const DOT_RENDER_ORDER: ReadonlyArray<DotState> = [
  'pending',
  'processed',
  'failed',
  'cancelled',
  'noTarget',
];

function dotClassFor(state: DotState): string {
  switch (state) {
    case 'pending':
      return 'dot';
    case 'processed':
      return 'dot is-processed';
    case 'failed':
      return 'dot is-failed';
    case 'cancelled':
      return 'dot is-cancelled';
    case 'noTarget':
      return 'dot is-no-target';
  }
}

interface SparkBucket {
  pending: number;
  processed: number;
  failed: number;
  cancelled: number;
  noTarget: number;
}

interface SparkBarRefs {
  readonly bar: HTMLElement;
  readonly pending: HTMLElement;
  readonly processed: HTMLElement;
  readonly failed: HTMLElement;
  readonly cancelled: HTMLElement;
  readonly noTarget: HTMLElement;
}

function makeSparkBucket(): SparkBucket {
  return { pending: 0, processed: 0, failed: 0, cancelled: 0, noTarget: 0 };
}

function bucketTotal(b: SparkBucket): number {
  return b.pending + b.processed + b.failed + b.cancelled + b.noTarget;
}

export interface Customer {
  readonly name: string;
  count: number;
  lastDoc: string | null;
  lastAt: number;
  firstAt: number;
  readonly el: HTMLElement;
  readonly countEl: HTMLElement;
  readonly subEl: HTMLElement;
  readonly timeEl: HTMLElement;
  /** 1 枚あたり / 1 件あたりのコスト表示。中身は index.ts が台帳から書く。 */
  readonly costEl: HTMLElement;
  readonly badgeEl: HTMLElement | null;
  readonly dotsEl: HTMLElement;
  readonly sparkEl: HTMLElement;
  readonly sparkBars: ReadonlyArray<SparkBarRefs>;
  sparkBuckets: SparkBucket[];
  badgeTimer: number | null;
  pendingCount: number;
  readonly items: Map<string, DotRef>;
}

interface RosterOptions {
  readonly bucketCount: number;
  readonly onNewBadgeExpire: (c: Customer) => void;
}

export type RosterSortMode = 'recency' | 'count';

export class Roster {
  private readonly customers: Map<string, Customer> = new Map();
  private readonly rawDataIndex: Map<string, Customer> = new Map();
  private inBatch = false;
  private readonly dirtyCustomers: Set<Customer> = new Set();
  private sortMode: RosterSortMode = 'recency';

  beginBatch(): void {
    this.inBatch = true;
  }

  endBatch(): void {
    for (const c of this.dirtyCustomers) {
      this.renderCustomerSpark(c);
      this.renderDots(c);
    }
    this.dirtyCustomers.clear();
    this.inBatch = false;
    this.reorderRoster();
  }

  private maybeRenderSpark(customer: Customer): void {
    if (this.inBatch) {
      this.dirtyCustomers.add(customer);
      return;
    }
    this.renderCustomerSpark(customer);
  }

  private maybeRenderDots(customer: Customer): void {
    if (this.inBatch) {
      this.dirtyCustomers.add(customer);
      return;
    }
    this.renderDots(customer);
  }

  constructor(
    private readonly dom: DomRefs,
    private readonly options: RosterOptions,
  ) {}

  getCustomers(): ReadonlyMap<string, Customer> {
    return this.customers;
  }

  clear(): void {
    for (const c of this.customers.values()) {
      if (c.badgeTimer !== null) window.clearTimeout(c.badgeTimer);
    }
    this.customers.clear();
    this.rawDataIndex.clear();
    this.dirtyCustomers.clear();
    this.dom.roster.innerHTML = '';
    this.dom.rosterEmpty.classList.remove('is-hidden');
  }

  touchCustomer(event: RawDataUploadEvent, bucketCount: number): Customer {
    const name = event.metadata.companyName;
    // lastAt は virtual 時刻 (event.timestamp ベース)。再生速度に追従して "just now" 等の経過表示が動く
    const vNow = new Date(event.timestamp).getTime();
    const existing = this.customers.get(name);
    const customer: Customer = existing ?? this.createCustomer(name, vNow, bucketCount);

    customer.count += 1;
    customer.lastDoc = event.metadata.documentName;
    customer.lastAt = vNow;
    // sparkBuckets の pending++ は addItemDot 側で raw_data 単位に行う

    customer.countEl.textContent = String(customer.count);
    customer.countEl.classList.remove('is-bumping');
    void customer.countEl.offsetWidth;
    customer.countEl.classList.add('is-bumping');

    customer.subEl.innerHTML = `<em>${escapeHtml(customer.lastDoc ?? '—')}</em>`;
    customer.timeEl.textContent = 'just now';

    customer.el.classList.remove('is-pulsing');
    void customer.el.offsetWidth;
    customer.el.classList.add('is-pulsing');
    window.setTimeout(() => customer.el.classList.remove('is-pulsing'), 700);

    if (!existing) {
      // 新規行は一旦 DOM に追加 (位置は reorder で確定)。is-entering アニメを走らせるため
      this.dom.roster.appendChild(customer.el);
      this.dom.rosterEmpty.classList.add('is-hidden');
    }

    // silent batch (シーク / loadDay の一気読み) 中はイベント毎に reorder しない。
    // endBatch で一度だけ並び替える
    if (!this.inBatch) {
      this.reorderRoster();
    }
    // 社数表示 (uniqueCount) は VolumeChart 側の getActiveCompanyCount を index.ts で
    // 250ms ごとに反映する。MAX_ROSTER で trim された後のサイズではないので注意

    return customer;
  }

  setSortMode(mode: RosterSortMode): void {
    if (this.sortMode === mode) return;
    this.sortMode = mode;
    this.reorderRoster();
  }

  private compareCustomers(a: Customer, b: Customer): number {
    if (this.sortMode === 'count') {
      if (b.count !== a.count) return b.count - a.count;
      return b.lastAt - a.lastAt;
    }
    return b.lastAt - a.lastAt;
  }

  private reorderRoster(): void {
    const sorted = [...this.customers.values()].sort((a, b) => this.compareCustomers(a, b));
    const keep = sorted.slice(0, MAX_ROSTER);
    const drop = sorted.slice(MAX_ROSTER);
    for (const c of drop) {
      if (c.badgeTimer !== null) window.clearTimeout(c.badgeTimer);
      this.customers.delete(c.name);
      c.el.remove();
    }
    // appendChild で同一要素を呼ぶと「移動」扱い (DOM 仕様)。順番に append すれば並び替わる
    for (const c of keep) {
      this.dom.roster.appendChild(c.el);
    }
  }

  addItemDot(event: RawDataUploadEvent): void {
    const customer = this.customers.get(event.metadata.companyName);
    if (!customer) return;
    const rawDataId = event.metadata.rawDataId;

    // sparkBuckets の最末尾に pending を 1 件足す
    const lastBucketIdx = customer.sparkBuckets.length - 1;
    const lastBucket = customer.sparkBuckets[lastBucketIdx];
    if (lastBucket) {
      lastBucket.pending += 1;
    }
    this.maybeRenderSpark(customer);

    const existing = customer.items.get(rawDataId);
    if (existing) {
      if (existing.state !== 'pending') {
        customer.pendingCount += 1;
        if (existing.bucketIdx >= 0) {
          const b = customer.sparkBuckets[existing.bucketIdx];
          if (b) {
            this.shiftBucketState(b, existing.state, 'pending');
          }
        }
      }
      existing.state = 'pending';
      this.refreshPendingFlag(customer);
      this.maybeRenderDots(customer);
      return;
    }

    customer.items.set(rawDataId, { state: 'pending', bucketIdx: lastBucketIdx });
    customer.pendingCount += 1;
    this.rawDataIndex.set(rawDataId, customer);
    this.refreshPendingFlag(customer);
    this.maybeRenderDots(customer);
  }

  private shiftBucketState(b: SparkBucket, from: DotState, to: DotState): void {
    if (from === to) return;
    if (b[from] > 0) b[from] -= 1;
    b[to] += 1;
  }

  updateItemState(
    id: string,
    newState: Exclude<DotState, 'pending'>,
  ): Customer | null {
    const customer = this.rawDataIndex.get(id);
    if (!customer) return null;
    const item = customer.items.get(id);
    if (!item) return null;
    const wasPending = item.state === 'pending';
    item.state = newState;
    if (wasPending) {
      customer.pendingCount = Math.max(0, customer.pendingCount - 1);
      this.refreshPendingFlag(customer);
      // sparkBuckets 上で状態遷移を反映 (pending → newState)
      if (item.bucketIdx >= 0) {
        const b = customer.sparkBuckets[item.bucketIdx];
        if (b) {
          this.shiftBucketState(b, 'pending', newState);
        }
      }
    }
    this.maybeRenderSpark(customer);
    this.maybeRenderDots(customer);

    // 緑(processed)/灰(cancelled)/青灰(noTarget) は数秒後、赤(failed) は 5 分後に items から消して再描画。
    // 「N 秒/分残る」は virtual 時間ベース。再生速度に比例して wall 時間を短くする。
    const baseDelay =
      newState === 'failed' ? FAILED_DOT_MS : FADE_TERMINAL_DOT_MS;
    const sp = Math.max(1, currentSpeed() ?? 1);
    const fadeDelay = baseDelay / sp;
    window.setTimeout(() => {
      if (customer.items.get(id) !== item) return;
      customer.items.delete(id);
      if (this.rawDataIndex.get(id) === customer) this.rawDataIndex.delete(id);
      this.maybeRenderDots(customer);
    }, fadeDelay);
    return customer;
  }

  rollAllSparks(direction: 1 | -1): void {
    for (const c of this.customers.values()) {
      if (direction === 1) {
        c.sparkBuckets.shift();
        c.sparkBuckets.push(makeSparkBucket());
        const expired: string[] = [];
        for (const [id, ref] of c.items) {
          ref.bucketIdx -= 1;
          if (ref.bucketIdx < 0) {
            // 8h 窓を抜けた: items / rawDataIndex から削除
            if (ref.state === 'pending') {
              c.pendingCount = Math.max(0, c.pendingCount - 1);
            }
            expired.push(id);
          }
        }
        for (const id of expired) {
          c.items.delete(id);
          if (this.rawDataIndex.get(id) === c) this.rawDataIndex.delete(id);
        }
        this.refreshPendingFlag(c);
      } else {
        c.sparkBuckets.pop();
        c.sparkBuckets.unshift(makeSparkBucket());
        for (const ref of c.items.values()) {
          ref.bucketIdx += 1;
        }
      }
      this.maybeRenderSpark(c);
      this.maybeRenderDots(c);
    }
  }

  resizeAllSparks(newLen: number): void {
    for (const c of this.customers.values()) {
      c.sparkBuckets = Array.from({ length: newLen }, makeSparkBucket);
      for (const ref of c.items.values()) {
        ref.bucketIdx = -1;
      }
      this.maybeRenderSpark(c);
    }
  }

  private renderDots(customer: Customer): void {
    const counts: Record<DotState, number> = {
      pending: 0,
      processed: 0,
      failed: 0,
      cancelled: 0,
      noTarget: 0,
    };
    for (const ref of customer.items.values()) {
      counts[ref.state] += 1;
    }
    customer.dotsEl.innerHTML = '';
    for (const state of DOT_RENDER_ORDER) {
      const total = counts[state];
      const big = Math.floor(total / DOT_GROUP_SIZE);
      const small = total % DOT_GROUP_SIZE;
      const klassBase = dotClassFor(state);
      for (let i = 0; i < small; i++) {
        const d = document.createElement('div');
        d.className = klassBase;
        customer.dotsEl.appendChild(d);
      }
      for (let i = 0; i < big; i++) {
        const d = document.createElement('div');
        d.className = `${klassBase} dot--big`;
        customer.dotsEl.appendChild(d);
      }
    }
  }

  private renderCustomerSpark(customer: Customer): void {
    let peak = 1;
    for (const b of customer.sparkBuckets) {
      const total = bucketTotal(b);
      if (total > peak) peak = total;
    }
    for (let i = 0; i < customer.sparkBuckets.length; i++) {
      const bar = customer.sparkBars[i];
      const b = customer.sparkBuckets[i];
      if (!bar || !b) continue;
      const total = bucketTotal(b);
      bar.bar.style.height = `${(total / peak) * 100}%`;
      const setSeg = (el: HTMLElement, n: number): void => {
        const pct = total > 0 ? (n / total) * 100 : 0;
        el.style.flexBasis = `${pct}%`;
      };
      setSeg(bar.pending, b.pending);
      setSeg(bar.processed, b.processed);
      setSeg(bar.failed, b.failed);
      setSeg(bar.cancelled, b.cancelled);
      setSeg(bar.noTarget, b.noTarget);
    }
  }

  private refreshPendingFlag(customer: Customer): void {
    customer.el.classList.toggle('has-pending', customer.pendingCount > 0);
  }

  private createCustomer(name: string, now: number, bucketCount: number): Customer {
    const li = document.createElement('li');
    li.className = 'row is-entering is-new';
    li.dataset['name'] = name;
    li.innerHTML = `
      <div class="row__count">0</div>
      <div class="row__info">
        <div class="row__name">
          <span class="row__color-chip"></span>
          <span class="row__name-text"></span>
          <span class="row__badge">NEW</span>
        </div>
        <div class="row__sub"></div>
      </div>
      <div class="row__dots"></div>
      <div class="row__spark"></div>
      <div class="row__tail">
        <div class="row__time">just now</div>
        <div class="row__cost"></div>
      </div>
    `;
    const nameText = requireQuerySelectorHTML(li, '.row__name-text');
    nameText.textContent = name;
    // 会社カラー: chart と同じ fnvHue 計算で色を統一
    const chip = requireQuerySelectorHTML(li, '.row__color-chip');
    chip.style.background = `hsl(${fnvHue(name)}, 62%, 58%)`;
    chip.title = name;

    const sparkEl = requireQuerySelectorHTML(li, '.row__spark');
    const sparkBars: SparkBarRefs[] = [];
    for (let i = 0; i < bucketCount; i++) {
      const bar = document.createElement('div');
      bar.className = 'spark__bar';
      // column-reverse のため、DOM 先頭が視覚下端になる。下から processed, pending, failed, cancelled, noTarget の順で積む
      const processed = document.createElement('div');
      processed.className = 'spark__seg spark__seg--processed';
      bar.appendChild(processed);
      const pending = document.createElement('div');
      pending.className = 'spark__seg spark__seg--pending';
      bar.appendChild(pending);
      const failed = document.createElement('div');
      failed.className = 'spark__seg spark__seg--failed';
      bar.appendChild(failed);
      const cancelled = document.createElement('div');
      cancelled.className = 'spark__seg spark__seg--cancelled';
      bar.appendChild(cancelled);
      const noTarget = document.createElement('div');
      noTarget.className = 'spark__seg spark__seg--no-target';
      bar.appendChild(noTarget);
      sparkEl.appendChild(bar);
      sparkBars.push({ bar, pending, processed, failed, cancelled, noTarget });
    }

    const badgeEl = li.querySelector('.row__badge');
    const badge = badgeEl instanceof HTMLElement ? badgeEl : null;

    const customer: Customer = {
      name,
      count: 0,
      lastDoc: null,
      lastAt: now,
      firstAt: now,
      el: li,
      countEl: requireQuerySelectorHTML(li, '.row__count'),
      subEl: requireQuerySelectorHTML(li, '.row__sub'),
      timeEl: requireQuerySelectorHTML(li, '.row__time'),
      costEl: requireQuerySelectorHTML(li, '.row__cost'),
      badgeEl: badge,
      dotsEl: requireQuerySelectorHTML(li, '.row__dots'),
      sparkEl,
      sparkBars,
      sparkBuckets: Array.from({ length: bucketCount }, makeSparkBucket),
      badgeTimer: null,
      pendingCount: 0,
      items: new Map(),
    };

    // NEW は virtual 時間ベース。再生速度で wall を短くするが「速すぎて気付かれない」を防ぐ最小値クランプ
    const newBadgeDelay = Math.max(2000, NEW_BADGE_MS / Math.max(1, currentSpeed() ?? 1));
    customer.badgeTimer = window.setTimeout(() => {
      customer.el.classList.remove('is-new');
      if (customer.badgeEl) customer.badgeEl.remove();
      this.options.onNewBadgeExpire(customer);
    }, newBadgeDelay);

    this.customers.set(name, customer);
    return customer;
  }
}

export function feedBodyFor(kind: DashboardEventKind, event: {
  readonly document?: string;
  readonly reason?: string;
}): string {
  if (kind === 'failed') {
    return event.reason ?? 'failure';
  }
  return event.document ?? '—';
}
